import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../app/store';
import {
  setLoading,
  setError,
  updateEmail,
  updateBell,
  updateBrowser,
  updatePopup,
  updateSoundEnabled,
  updateSoundName,
} from '../../features/integrations/notificationSlice';
import { getAuthContext } from '../../utils/jwt';
import chime from '../../assets/sounds/chime.mp3';
import ping from '../../assets/sounds/ping.mp3';
import alert from '../../assets/sounds/alert.mp3';
import got from '../../assets/sounds/got.mp3';
import { FaPlay, FaStop } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../utils/constants';

const FaPlayComponent = FaPlay as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

const FaStopComponent = FaStop as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;


const generateIdempotencyKey = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}-${Math.random().toString(36).substring(2, 15)}`;
};

const Notifications: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Get state from Redux
  const {
    email,
    bell,
    browser,
    popup,
    soundEnabled,
    soundName,
    loading,
    // error,
  } = useSelector((state: RootState) => state.notifications2);

  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- API CALL 1: FETCH DATA ---
  const fetchPreferences = useCallback(async () => {
    dispatch(setLoading(true));
    dispatch(setError(null));
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const response = await fetch(`${BASE_URL}/api/settings/notifications/preferences`, {
        method: 'GET',
        headers: {
          'X-Idempotency-Key': generateIdempotencyKey(),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
      const json = await response.json();

      // Handle new API response structure
      // The preferences are nested in data.preferences.UPDATES
      const preferences = json?.data?.preferences?.UPDATES || {};

      // Map the API fields to Redux state
      // API uses: SOUND, POPUP, IN_APP, BROWSER, EMAIL
      // Redux uses: email, bell, browser, popup, soundEnabled
      if (preferences.EMAIL !== undefined) dispatch(updateEmail(preferences.EMAIL));
      if (preferences.IN_APP !== undefined) dispatch(updateBell(preferences.IN_APP)); // Map IN_APP to bell
      if (preferences.BROWSER !== undefined) dispatch(updateBrowser(preferences.BROWSER));
      if (preferences.POPUP !== undefined) dispatch(updatePopup(preferences.POPUP));
      if (preferences.SOUND !== undefined) dispatch(updateSoundEnabled(preferences.SOUND));

      // Note: soundName might not be in the API response, keep the current value

    } catch (err: any) {
      dispatch(setError(err.message));
      toast.error(err.message);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch]);

  // --- API CALL 2: SAVE DATA ---
  // 🔴 UNCHANGED - Keep the original payload structure
  const savePreferences = useCallback(async () => {
    try {
      const { token, tenantId, userId } = getAuthContext();
      if (!token || !tenantId) throw new Error('Authentication required');

      const state = (await import('../../app/store')).store.getState() as RootState;
      const currentState = state.notifications2;

      const payload = {
        email: currentState.email,
        bell: currentState.bell,
        browser: currentState.browser,
        popup: currentState.popup,
        soundEnabled: currentState.soundEnabled,
        soundName: currentState.soundName
      };

      const response = await fetch(`${BASE_URL}/api/settings/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'X-Idempotency-Key': generateIdempotencyKey(),
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMessage = `Failed to update: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) { /* ignore */ }
        throw new Error(errorMessage);
      }

      toast.success('Notification preferences updated successfully!');

    } catch (err: any) {
      dispatch(setError(err.message));
      toast.error(err.message);
    }
  }, [dispatch]);

  // Run fetch on mount
  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  // Handle toggle for any preference - Updates Redux immediately, saves later
  const handleToggle = (type: string, value: boolean) => {
    // Update Redux state instantly
    switch (type) {
      case 'email': dispatch(updateEmail(value)); break;
      case 'bell': dispatch(updateBell(value)); break;
      case 'browser': dispatch(updateBrowser(value)); break;
      case 'popup': dispatch(updatePopup(value)); break;
      case 'sound': dispatch(updateSoundEnabled(value)); break;
      default: break;
    }

    // Debounce: Wait 1.5 seconds after user stops clicking to save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      savePreferences();
    }, 1500);
  };

  // Handle sound option change
  const handleSoundOptionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSoundName = e.target.value;
    dispatch(updateSoundName(newSoundName));

    // Debounce save
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      savePreferences();
    }, 1500);
  };

  // Play or pause sound
  const handleToggleSound = () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    let soundFile = '';
    if (soundName === 'Chime (1 sec)') soundFile = chime;
    else if (soundName === 'Ping (2 sec)') soundFile = ping;
    else if (soundName === 'Alert (3 sec)') soundFile = alert;
    else if (soundName === 'Got') soundFile = got;

    if (soundFile) {
      const audio = new Audio(soundFile);
      audioRef.current = audio;
      audio.play();
      setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  if (loading) {
    return (
      <div className='flex justify-center items-center py-12'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#00899E]'></div>
      </div>
    );
  }

  return (
    <div>
      <h2 className='text-[24px] font-bold text-[#3F5265] mb-2'>
        Notifications
      </h2>
      <p className='text-[#657584] text-[16px] mb-6'>
        These preferences will only be applied to you.
      </p>

      <div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
        <h3 className='text-[20px] font-semibold text-[#3F5265] mb-2'>
          How you get notified
        </h3>
        <p className='text-[16px] text-[#657584] mb-6'>
          Choose where you want to see your notifications
        </p>

        <div className='space-y-6'>
          {/* Email */}
          <div className='flex justify-between items-center'>
            <div>
              <p className='text-[#3F5265] font-medium text-[16px]'>Email</p>
              <p className='text-[12px] text-[#657584]'>Sent to your email address</p>
            </div>
            <label className='inline-flex items-center cursor-pointer'>
              <input type='checkbox' checked={email} onChange={() => handleToggle('email', !email)} className='sr-only' />
              <div className={`w-11 h-6 rounded-full transition ${email ? 'bg-[#00A4BD]' : 'bg-gray-300'} relative`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${email ? 'translate-x-6' : 'translate-x-1'}`}></div>
              </div>
            </label>
          </div>

          {/* Bell */}
          <div className='flex justify-between items-center'>
            <div>
              <p className='text-[#3F5265] font-medium text-[16px]'>Bell</p>
              <p className='text-[12px] text-[#657584]'>Show up in the bell icon in the Autopath navigation bar.</p>
            </div>
            <label className='inline-flex items-center cursor-pointer'>
              <input type='checkbox' checked={bell} onChange={() => handleToggle('bell', !bell)} className='sr-only' />
              <div className={`w-11 h-6 rounded-full transition ${bell ? 'bg-[#00A4BD]' : 'bg-gray-300'} relative`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${bell ? 'translate-x-6' : 'translate-x-1'}`}></div>
              </div>
            </label>
          </div>

          {/* Browser */}
          <div className='flex justify-between items-center'>
            <div>
              <p className='text-[#3F5265] font-medium text-[16px]'>Browser</p>
              <p className='text-[12px] text-[#657584]'>Appear on screen when you're not active in Autopath.</p>
            </div>
            <label className='inline-flex items-center cursor-pointer'>
              <input type='checkbox' checked={browser} onChange={() => handleToggle('browser', !browser)} className='sr-only' />
              <div className={`w-11 h-6 rounded-full transition ${browser ? 'bg-[#00A4BD]' : 'bg-gray-300'} relative`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${browser ? 'translate-x-6' : 'translate-x-1'}`}></div>
              </div>
            </label>
          </div>

          {/* Pop-up */}
          <div className='flex justify-between items-center'>
            <div>
              <p className='text-[#3F5265] font-medium text-[16px]'>Pop-up</p>
              <p className='text-[12px] text-[#657584]'>Appear on your screen for a few seconds when you're active.</p>
            </div>
            <label className='inline-flex items-center cursor-pointer'>
              <input type='checkbox' checked={popup} onChange={() => handleToggle('popup', !popup)} className='sr-only' />
              <div className={`w-11 h-6 rounded-full transition ${popup ? 'bg-[#00A4BD]' : 'bg-gray-300'} relative`}>
                <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${popup ? 'translate-x-6' : 'translate-x-1'}`}></div>
              </div>
            </label>
          </div>

          {/* Sound */}
          <div className='flex justify-between items-center'>
            <div>
              <p className='text-[#3F5265] font-medium text-[16px]'>🎵 Sound</p>
            </div>
            <div className='flex items-center gap-3'>
              <select value={soundName} onChange={handleSoundOptionChange} className='border border-[#E1EAF0] rounded px-2 py-1 text-[14px] text-[#3F5265]'>
                <option>Chime (1 sec)</option>
                <option>Ping (2 sec)</option>
                <option>Alert (3 sec)</option>
                <option>Got</option>
              </select>
              <button onClick={handleToggleSound} className='flex items-center gap-1 border border-[#00A4BD] text-[#00A4BD] px-4 py-1 rounded hover:bg-[#E0F8FB]'>
                {isPlaying ? <FaStopComponent className='text-red-600' /> : <FaPlayComponent className='text-green-600' />}
                <span>{isPlaying ? 'Stop' : 'Play'} </span>
              </button>
              <label className='inline-flex items-center cursor-pointer'>
                <input type='checkbox' checked={soundEnabled} onChange={() => handleToggle('sound', !soundEnabled)} className='sr-only' />
                <div className={`w-11 h-6 rounded-full transition ${soundEnabled ? 'bg-[#00A4BD]' : 'bg-gray-300'} relative`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${soundEnabled ? 'translate-x-6' : 'translate-x-1'}`}></div>
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;